import { CardContent, CardHeader } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';

import {
  useGetDirectEMRIntegrations,
  useGetEMRConnections,
} from '@hooks/api/emr-connections';
import { useGetUser } from '@hooks/api/user';
import { useSession } from '@hooks/useSession';

import { formatDate } from '@utils/format';
import { formatName } from '@utils/format';

const messages = defineMessages({
  connectEMR: {
    id: 'clientProfileCard.connectEMR',
    defaultMessage: 'Connected EMRs (Hospitals and Clinics)',
  },
  subtitle: {
    id: 'clientProfileCard.emrSubtitle',
    defaultMessage: '{name} {text} hospitals and clinics connected',
  },
});

const EMRConnectionsCard: React.FC<{ user_id: string }> = ({ user_id }) => {
  const { formatMessage } = useIntl();
  const {
    state: { activeProfile },
  } = useSession();

  const { data: emrConnectionsData, loading: emrConnectionsLoading } =
    useGetEMRConnections({
      vitall_patient_id: activeProfile?.vitall_patient_id ?? '',
    });
  const { data: emrIntegrationsData, loading: emrIntegrationsLoading } =
    useGetDirectEMRIntegrations();

  const { data: userData, loading: userLoading } = useGetUser({
    id: user_id,
  });
  const user = userData?.getUserRecord;
  const { first_name, last_name } = user ?? {};
  const loading =
    userLoading || emrConnectionsLoading || emrIntegrationsLoading;

  const emrConnections =
    emrConnectionsData?.map(
      ({ created_at, updated_at, fhir_api_endpoint_id, patient }) => {
        const emrConnection = emrIntegrationsData?.find(
          (integration) =>
            integration.fhir_api_endpoint_id === fhir_api_endpoint_id
        );
        return {
          connection: emrConnection?.organization_name ?? '-',
          updated_at,
          created_at,
          emr_patient_id: patient,
        };
      }
    ) ?? [];

  return (
    <ContentLoader loading={loading}>
      <CardHeader
        subheader={formatMessage(messages.subtitle, {
          name: formatName(first_name, last_name),
          text: emrConnections.length
            ? 'has the following'
            : 'does not have any',
        })}
        title={formatMessage(messages.connectEMR)}
        titleTypographyProps={{
          color: 'primary.main',
        }}
      />
      <CardContent>
        {emrConnections.length > 0 && (
          <MuiDataTable
            columns={[
              {
                name: 'connection',
                label: 'Connection',
              },
              {
                name: 'created_at',
                className: 'small',
                label: 'Date Connected',
                options: {
                  customBodyRender: (value: string) =>
                    formatDate({
                      date: value,
                    }),
                },
              },
              {
                name: 'updated_at',
                className: 'small',
                label: 'Last Update',
                options: {
                  customBodyRender: (value?: string) =>
                    value
                      ? formatDate({
                          date: value,
                        })
                      : '-',
                },
              },
              {
                name: 'emr_patient_id',
                className: 'small',
                label: 'EMR Patient ID',
              },
            ]}
            data={emrConnections}
            options={{
              customFooter: () => null,
            }}
          />
        )}
      </CardContent>
    </ContentLoader>
  );
};

export default EMRConnectionsCard;
